import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, CircleNotch } from '@phosphor-icons/react';
import { API } from '../context/AuthContext';

/**
 * Phase 2.5a — Magic-link consume page.
 *
 * Builder lands here from /builder/auth/consume?token=xxx (the link in
 * their email). We POST the token to the backend, which mints a session
 * cookie and returns the builder profile. On success we redirect into
 * the dashboard. On failure we show a clear error + a retry path.
 */
export default function BuilderConsume() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [state, setState] = useState('checking');  // 'checking' | 'ok' | 'expired' | 'used' | 'error'
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        const token = params.get('token');
        if (!token) { setState('error'); setErrorMsg('No sign-in token found in the link.'); return; }
        let cancelled = false;
        (async () => {
            try {
                await axios.post(
                    `${API}/builder/auth/consume`,
                    { token },
                    { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
                );
                if (cancelled) return;
                setState('ok');
                // Brief celebratory beat before navigation so the
                // success state is actually perceived rather than
                // flashing past.
                setTimeout(() => navigate('/builder/dashboard', { replace: true }), 600);
            } catch (err) {
                if (cancelled) return;
                const detail = err?.response?.data?.detail || '';
                if (/expired/i.test(detail)) setState('expired');
                else if (/already been used/i.test(detail)) setState('used');
                else { setState('error'); setErrorMsg(detail || 'Sign-in failed.'); }
            }
        })();
        return () => { cancelled = true; };
    }, [params, navigate]);

    return (
        <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center px-4">
            <div className="w-full max-w-md text-center" data-testid="builder-consume">
                <img src="/defectr-logo.png" alt="DefectR" className="h-8 w-auto mx-auto mb-8" />

                {state === 'checking' && (
                    <>
                        <CircleNotch size={36} weight="bold" color="#1FA5CF" className="animate-spin mx-auto" />
                        <p className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-[#52525B]">Signing you in…</p>
                    </>
                )}

                {state === 'ok' && (
                    <>
                        <CheckCircle size={36} weight="fill" color="#1FA5CF" className="mx-auto" />
                        <p className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-[#1FA5CF]">Signed in — taking you to your plots</p>
                    </>
                )}

                {(state === 'expired' || state === 'used' || state === 'error') && (
                    <>
                        <XCircle size={36} weight="fill" color="#E53935" className="mx-auto" />
                        <h1 className="mt-5 font-display text-2xl font-semibold tracking-tight">
                            {state === 'expired' ? 'Link expired' : state === 'used' ? 'Link already used' : 'Sign-in failed'}
                        </h1>
                        <p className="mt-3 text-sm text-[#52525B]">
                            {state === 'expired' && 'Magic links expire after 15 minutes. Request a fresh one and try again.'}
                            {state === 'used' && 'This link only works once. If you didn\'t use it, request a new one — sometimes mail clients pre-load links.'}
                            {state === 'error' && (errorMsg || 'Something went wrong.')}
                        </p>
                        <Link
                            to="/builder/login"
                            data-testid="builder-consume-retry"
                            className="mt-6 inline-block bg-[#1FA5CF] hover:bg-[#1787AB] text-white px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] active:scale-95 transition-all duration-150 no-underline"
                        >Send a new link</Link>
                    </>
                )}
            </div>
        </div>
    );
}
